/**
 * Deals Page - Pipeline View
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { formatCurrency } from '@/lib/utils';
import { Plus } from 'lucide-react';

interface PipelineStage {
  stage: string;
  dealCount: number;
  totalValue: number;
  weightedValue: number;
}

interface Deal {
  id: string;
  accountId: string;
  accountName: string;
  title: string;
  value: number;
  stage: string;
  probability: number;
  expectedCloseDate: string | null;
  ownerName: string | null;
}

const stageLabels: Record<string, string> = {
  lead: 'Lead',
  qualified: 'Qualified',
  proposal: 'Proposal',
  negotiation: 'Negotiation',
  closed_won: 'Closed Won',
  closed_lost: 'Closed Lost',
};

const stageColors: Record<string, string> = {
  lead: 'bg-gray-100 border-gray-300',
  qualified: 'bg-blue-50 border-blue-300',
  proposal: 'bg-yellow-50 border-yellow-300',
  negotiation: 'bg-orange-50 border-orange-300',
  closed_won: 'bg-green-50 border-green-300',
  closed_lost: 'bg-red-50 border-red-300',
};

export function DealsPage() {
  const { data: pipelineData, isLoading: pipelineLoading } = useQuery({
    queryKey: ['pipeline'],
    queryFn: async () => {
      const result = await api.getPipelineSummary();
      return result;
    },
  });

  const { data: dealsData, isLoading: dealsLoading } = useQuery({
    queryKey: ['deals'],
    queryFn: async () => {
      const result = await api.getDeals({ limit: 100 });
      return result;
    },
  });

  const pipeline = (pipelineData?.data || []) as PipelineStage[];
  const deals = (dealsData?.data || []) as Deal[];

  const totalPipelineValue = pipeline.reduce((sum, s) => sum + s.totalValue, 0);
  const totalWeightedValue = pipeline.reduce((sum, s) => sum + s.weightedValue, 0);

  const stages = ['lead', 'qualified', 'proposal', 'negotiation'];

  const getDealsByStage = (stage: string) => deals.filter((d) => d.stage === stage);

  if (pipelineLoading || dealsLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Deals Pipeline</h1>
          <p className="text-muted-foreground">Manage your sales opportunities</p>
        </div>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          New Deal
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Total Pipeline Value</p>
            <p className="text-2xl font-bold">{formatCurrency(totalPipelineValue)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Weighted Value</p>
            <p className="text-2xl font-bold">{formatCurrency(totalWeightedValue)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Open Deals</p>
            <p className="text-2xl font-bold">{deals.filter((d) => !d.stage.startsWith('closed')).length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Pipeline Board */}
      <div className="grid gap-4 md:grid-cols-4">
        {stages.map((stage) => {
          const stageDeals = getDealsByStage(stage);
          const stageTotal = stageDeals.reduce((sum, d) => sum + d.value, 0);

          return (
            <div key={stage} className={`rounded-lg border-2 ${stageColors[stage]} p-4`}>
              <div className="mb-4">
                <h3 className="font-medium">{stageLabels[stage]}</h3>
                <p className="text-sm text-muted-foreground">
                  {stageDeals.length} deals • {formatCurrency(stageTotal)}
                </p>
              </div>

              <div className="space-y-2">
                {stageDeals.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No deals</p>
                ) : (
                  stageDeals.map((deal) => (
                    <Card key={deal.id} className="cursor-pointer hover:shadow-md transition-shadow">
                      <CardContent className="p-3">
                        <h4 className="font-medium text-sm truncate">{deal.title}</h4>
                        <p className="text-xs text-muted-foreground truncate">{deal.accountName}</p>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-sm font-medium">{formatCurrency(deal.value)}</span>
                          <span className="text-xs text-muted-foreground">{deal.probability}%</span>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
